import React, { useState } from "react";
import { DashboardLayout, MetricsCard } from "src/components/dashboard";
import { Input } from "src/components/inputs";
import { Flex } from "src/components/ui";
import searchIcon from "src/assets/images/input/searchIcon.svg";
import { theme } from "src/styles/Theme";
import { DashboardContainer } from "../dashboard/Dashboard";
import { ScaleLoader } from "react-spinners";
import user from "src/assets/images/metrics/user.svg";
import artisan from "src/assets/images/metrics/artisan.svg";
import wallet from "src/assets/images/metrics/wallet.svg";
import RecentBookingsTable from "./RecentBookingsTable";
import CustomerTransactionsTable from "./CustomerTransactionTable";
import VendorTransactionsTable from "./VendorTransactionTable";
import { toast } from "react-toastify";
import { numberWithCommas } from "src/utils";
import { useEffect } from "react";
import {
  useGetArtisanWalletDataQuery,
  useGetCustomerWalletDataQuery,
  useGetBookingWalletDataQuery,
  useGetBookingTransactionsQuery,
} from "src/store";

interface Props {
  handleChange: (e: any) => void;
}

export const RhsHeading: React.FC<Props> = ({ handleChange }) => (
  <Flex wrap="wrap">
    <Input
      icon={<img src={searchIcon} alt="searchIcon" />}
      type="search"
      placeholder="Search"
      handleChange={handleChange}
    />
  </Flex>
);

type WalletSummaryTypes = {
  customerBalance: number;
  vendorBalance: number;
  escrowBalance: number;
  commitmentFee: number;
  serviceFee: number;
  verificationBalance: number;
};

const Wallet = () => {
  const [searchField, setSearchField] = useState("");



  // RTK Query hooks
  const {
    data: artisanWalletData,
    isLoading: isLoadingArtisanWallet,
    error: artisanWalletError,
    refetch: refetchArtisanWallet,
  } = useGetArtisanWalletDataQuery();

  const {
    data: customerWalletData,
    isLoading: isLoadingCustomerWallet,
    error: customerWalletError,
    refetch: refetchCustomerWallet,
  } = useGetCustomerWalletDataQuery();

  const {
    data: bookingWalletData,
    isLoading: isLoadingBookingWallet,
    error: bookingWalletError,
    refetch: refetchBookingWallet,
  } = useGetBookingWalletDataQuery();

  const {
    data: bookingTransactions,
    isLoading: isLoadingBookingTransactions,
    error: bookingTransactionsError,
    refetch: refetchBookingTransactions,
  } = useGetBookingTransactionsQuery();

  // Combined loading states
  const isAnyLoading =
    isLoadingArtisanWallet ||
    isLoadingCustomerWallet ||
    isLoadingBookingWallet ||
    isLoadingBookingTransactions;

  // Error handling with retry functionality
  React.useEffect(() => {
    if (artisanWalletError) {
      toast.error("Failed to load artisan wallet data");
    }
    if (customerWalletError) {
      toast.error("Failed to load customer wallet data");
    }
    if (bookingWalletError) {
      toast.error("Failed to load booking wallet data");
    }
    if (bookingTransactionsError) {
      toast.error("Failed to load booking transactions");
    }
  }, [
    artisanWalletError,
    customerWalletError,
    bookingWalletError,
    bookingTransactionsError,
  ]);

  // Function to refetch all data
  const refetchAllData = () => {
    refetchArtisanWallet();
    refetchCustomerWallet();
    refetchBookingWallet();
    refetchBookingTransactions();
    toast.info("Refreshing wallet data...");
  };

  const handleChange = (e: any) => {
    setSearchField(e.target.value);
  };

  const twoDecimalPoint = (number: number) => {
    const trimmedNum = Number(number).toFixed(2);
    return Number(trimmedNum);
  };

  // Debug logging to see the data structure
  console.log("Debug - Artisan Wallet Data:", artisanWalletData);
  console.log("Debug - Customer Wallet Data:", customerWalletData);
  console.log("Debug - Booking Wallet Data:", bookingWalletData);
  console.log("Debug - Booking Transactions:", bookingTransactions);

  // Extract data from RTK Query responses with proper fallbacks
  const metricData: WalletSummaryTypes = {
    customerBalance: customerWalletData?.payload?.data?.balance || 0,
    vendorBalance: artisanWalletData?.payload?.data?.balance || 0,
    escrowBalance: bookingWalletData?.payload?.data?.escrow_balance_amount || 0,
    commitmentFee:
      bookingWalletData?.payload?.data?.commitment_fee_commission || 0,
    serviceFee: bookingWalletData?.payload?.data?.service_fee_commission || 0,
    verificationBalance:
      bookingWalletData?.payload?.data?.verification_fee_amount || 0,
  };

  // Extract transaction data with proper fallbacks and ensure arrays
  // Note: artisanWalletData contains both balance and transactions
  const customerRecentWalletTrnx =
    customerWalletData?.payload?.data?.transactions || [];
  const vendorRecentWalletTrnx =
    artisanWalletData?.payload?.data?.transactions || [];
  const recentBookings = bookingTransactions?.payload?.data?.transactions || [];

  // Ensure all data is arrays to prevent .slice() errors
  const safeCustomerTransactions = Array.isArray(customerRecentWalletTrnx)
    ? customerRecentWalletTrnx
    : [];
  const safeVendorTransactions = Array.isArray(vendorRecentWalletTrnx)
    ? vendorRecentWalletTrnx
    : [];
  const safeBookings = Array.isArray(recentBookings) ? recentBookings : [];
    useEffect(() => {
    document.title = "Wallet Page";
  }, []);

  const metrics = [
    {
      count: numberWithCommas(metricData?.customerBalance),
      key: "Total Customer Wallet balance",
      img: user,
      color: theme.colors.purple,
      href: "/customers",
    },
    {
      count: numberWithCommas(twoDecimalPoint(metricData?.vendorBalance)),
      key: "Total Vendor wallet balance",
      img: artisan,
      color: theme.colors.blue,
      href: "/vendors",
    },
    {
      count:
        metricData?.escrowBalance === 0
          ? metricData?.escrowBalance
          : numberWithCommas(metricData?.escrowBalance),
      key: "Escrow Wallet Balance ",
      img: wallet,
      color: theme.colors.cyan,
      href: "/bookings?tabStatus=active",
    },
    {
      count:
        metricData?.serviceFee === 0
          ? metricData?.serviceFee
          : numberWithCommas(metricData?.serviceFee),
      key: "Total Service Fee commission",
      img: wallet,
      color: theme.colors.purple,
      href: "/kyc",
    },
    {
      count:
        metricData?.commitmentFee === 0
          ? metricData?.commitmentFee
          : numberWithCommas(metricData?.commitmentFee),
      key: "Total Commitment Fee",
      img: wallet,
      color: theme.colors.blue,
      href: "/bookings?tabStatus=all",
    },
    {
      count: numberWithCommas(metricData?.verificationBalance),
      key: "Total Verification Fee",
      img: wallet,
      color: theme.colors.blue,
      href: "/bookings?tabStatus=all",
    },
  ];

  return (
    <DashboardLayout
      pageTitle="Wallet"
      // rhsHeading={<RhsHeading handleChange={handleChange} />}
    >
      <DashboardContainer>
        {/* Refresh button */}

        <div className="metrics__cards">
          <Flex wrap="wrap" gap="1.5rem">
            {metrics.map((metric) => {
              return (
                <MetricsCard
                  key={metric.key}
                  metric={metric}
                  href={metric.href}
                />
              );
            })}
          </Flex>
        </div>

        {/* Loading state for all data */}
        {isAnyLoading && (
          <div className="loader-container">
            <ScaleLoader color="#7E00C4" height={50} width={8} />
            <p className="text-center mt-4 text-gray-600">
              Loading wallet data...
            </p>
          </div>
        )}

        {/* Tables - only show when not loading */}
        {!isAnyLoading && (
          <>
            <RecentBookingsTable rows={safeBookings} />
            <CustomerTransactionsTable rows={safeCustomerTransactions} />
            <VendorTransactionsTable rows={safeVendorTransactions} />
          </>
        )}
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default Wallet;
